<template>
  <div
    class="resource-media"
    :class="[`ctx-${context}`, `fit-${fit}`]"
    @click="$emit('open')"
  >
    <!-- URL still resolving -> discreet loading placeholder -->
    <div v-if="isLoading" class="rm-placeholder" aria-hidden="true">
      <span class="rm-spinner"></span>
    </div>

    <!-- image -->
    <img
      v-else-if="mediaType === 'image'"
      class="rm-media"
      :src="url"
      :alt="displayTitle"
      loading="lazy"
    />

    <!-- video -->
    <video
      v-else-if="mediaType === 'video'"
      ref="videoEl"
      class="rm-media"
      :src="url"
      :controls="videoHasControls"
      :muted="!videoHasControls"
      :loop="!videoHasControls"
      :autoplay="context === 'lightbox'"
      playsinline
      preload="metadata"
      @click="onControlsClick"
      @play="$emit('media-play')"
      @pause="$emit('media-pause')"
    ></video>

    <!-- audio -> styled card -->
    <div v-else-if="mediaType === 'audio'" class="rm-audio-card">
      <span class="rm-audio-icon" v-html="ICON_AUDIO"></span>
      <span v-if="displayTitle" class="rm-audio-title">{{ displayTitle }}</span>
      <audio
        class="rm-audio-player"
        :src="url"
        controls
        @click="onControlsClick"
        @play="$emit('media-play')"
        @pause="$emit('media-pause')"
      ></audio>
    </div>

    <!-- pdf in lightbox -> full-height embed -->
    <embed
      v-else-if="isPdf && context === 'lightbox'"
      class="rm-embed"
      :src="url"
      type="application/pdf"
    />

    <!-- pdf thumbnail/inline -> first page rendered on a canvas -->
    <canvas
      v-else-if="isPdf && !pdfFailed"
      ref="pdfCanvasEl"
      class="rm-media rm-canvas"
      :aria-label="displayTitle"
    ></canvas>

    <!-- generic file tile: failed URL, non-pdf documents, other formats,
         or a PDF whose first page could not be rendered -->
    <div v-else class="rm-file-tile">
      <span class="rm-file-icon" v-html="fileIcon"></span>
      <span class="rm-file-name">{{ fileLabel }}</span>
    </div>
  </div>
</template>

<script>
/*
 * Cache au niveau module — réellement partagé entre TOUTES les instances du
 * composant : url -> promesse d'un canvas hors-écran contenant la page 1 du
 * PDF, pour que le même PDF ne soit jamais parsé/rendu deux fois.
 */
const pdfPageCache = new Map();

async function renderPdfFirstPage(url) {
  if (pdfPageCache.has(url)) return pdfPageCache.get(url);
  const promise = (async () => {
    // Lazy dynamic import keeps the heavy pdf.js worker out of the main bundle
    // for galleries that contain no PDFs. pdf.js v2.6.347 + webpack 4: import the
    // real build files by path (the package `exports` field is not resolved).
    // Importing `pdf.worker.entry` registers `window.pdfjsWorker`, which pdf.js
    // picks up as a main-thread worker — no separate workerSrc URL is required.
    const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf');
    await import(/* webpackChunkName: "pdfjs-worker" */ 'pdfjs-dist/build/pdf.worker.entry');
    const pdf = await pdfjsLib.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(3, Math.max(1, 900 / base.width));
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    return canvas;
  })();
  pdfPageCache.set(url, promise);
  // Let a later attempt retry if this render fails (e.g. transient fetch error).
  promise.catch(() => pdfPageCache.delete(url));
  return promise;
}
</script>

<script setup>
import { computed, defineProps, defineEmits, onBeforeUnmount, ref, watch } from 'vue';
import { getMediaType, getExtension } from '../../utils/mediaType';
import { cleanText } from '../../utils/galleryText';

/*
 * ResourceMedia — renders one exhibition resource whatever its format
 * (image / video / audio / PDF / other document / unknown) inside any frame.
 * Neutral, self-contained chrome so it can sit inside very different gallery
 * art directions. Emits `open` on click (templates use it to open a lightbox).
 */

const props = defineProps({
  // { name, title, description } — `name` (file name) drives type detection.
  resource: { type: Object, default: () => ({}) },
  // Resolved media URL. '' (or undefined) => still loading; null => S3 fetch failed.
  url: { type: String, default: '' },
  // Rendering context: 'thumb' | 'inline' | 'lightbox'.
  context: { type: String, default: 'thumb' },
  // object-fit for image/video/pdf-canvas: 'cover' | 'contain'.
  fit: { type: String, default: 'cover' },
  // Pilotage de la lecture vidéo par le template (chapitre actif, photogramme
  // centré, œuvre éclairée…) :
  //   - thumb  : lecture quand visible ET `active !== false` ; `false` = pause
  //              immédiate ; `null` (défaut) = comportement historique
  //              (visibilité seule).
  //   - inline : passage à `true` = tentative de lecture EN MUET (les controls
  //              restent pour réactiver le son) ; `false` (ou démontage) =
  //              pause ; `null` = pas d'autoplay.
  //   - lightbox : ignoré.
  // L'audio n'autoplay jamais, quel que soit `active`.
  active: { type: Boolean, default: null },
});

// `media-play` / `media-pause` sont relayés depuis les événements natifs
// play/pause des éléments <video> et <audio>, dans tous les contextes.
defineEmits(['open', 'media-play', 'media-pause']);

// Inline SVGs (currentColor) keep the component free of any icon-font dependency.
const ICON_AUDIO = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"/></svg>';
const ICON_DOC = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm0 2 4 4h-4V4zM8 13h8v2H8v-2zm0 4h8v2H8v-2z"/></svg>';
const ICON_FILE = '<svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm0 2 4 4h-4V4z"/></svg>';

const videoEl = ref(null);
const pdfCanvasEl = ref(null);
const pdfFailed = ref(false);

const isLoading = computed(() => props.url === '' || props.url === undefined);
const isFailed = computed(() => props.url === null);
const mediaType = computed(() => (isFailed.value ? 'other' : getMediaType(props.resource?.name)));
// `false` when the URL failed so a broken PDF falls through to the file tile
// (rather than an embed/canvas with no source).
const isPdf = computed(() => !isFailed.value && getExtension(props.resource?.name) === 'pdf');

const displayTitle = computed(
  () => cleanText(props.resource?.title) || cleanText(props.resource?.name)
);
const fileLabel = computed(
  () => cleanText(props.resource?.title) || cleanText(props.resource?.name) || 'Fichier'
);
const fileIcon = computed(() => (mediaType.value === 'doc' ? ICON_DOC : ICON_FILE));

// Videos get native controls everywhere except in thumbnails (autoplay-on-visible).
const videoHasControls = computed(() => props.context !== 'thumb');

// Don't let clicks on native controls (audio/video) bubble up as an `open`.
const onControlsClick = (event) => {
  if (props.context !== 'thumb') event.stopPropagation();
};

/* ----------------------------- video playback ---------------------------- */
const safePlay = (el) => {
  const playback = el.play();
  if (playback && typeof playback.catch === 'function') playback.catch(() => {});
};

// thumb: play only while visible (IntersectionObserver) AND not explicitly
// deactivated by the template (`active === false`).
const thumbVisible = ref(false);
let videoObserver = null;
const teardownVideoObserver = () => {
  if (videoObserver) {
    videoObserver.disconnect();
    videoObserver = null;
  }
};

const syncThumbPlayback = () => {
  const el = videoEl.value;
  if (!el || props.context !== 'thumb') return;
  if (thumbVisible.value && props.active !== false) {
    safePlay(el);
  } else {
    el.pause();
  }
};

const setupVideoObserver = (el) => {
  teardownVideoObserver();
  thumbVisible.value = false;
  if (!el || mediaType.value !== 'video' || props.context !== 'thumb') return;
  // Set the property (not just the attribute) so browsers allow autoplay.
  el.muted = true;
  if (typeof IntersectionObserver === 'undefined') return;
  videoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        thumbVisible.value = entry.isIntersecting;
      });
      syncThumbPlayback();
    },
    { threshold: 0.25 }
  );
  videoObserver.observe(el);
};

// inline: `active === true` => attempt a MUTED play (controls stay visible so
// the user can unmute); leaving `true` or receiving `false` => pause.
const applyInlineActive = () => {
  const el = videoEl.value;
  if (!el || props.context !== 'inline' || mediaType.value !== 'video') return;
  if (props.active === true) {
    el.muted = true;
    safePlay(el);
  }
};

watch(videoEl, (el) => {
  setupVideoObserver(el);
  applyInlineActive();
});

watch(
  () => props.active,
  (active, previous) => {
    const el = videoEl.value;
    if (!el || mediaType.value !== 'video') return;
    if (props.context === 'thumb') {
      syncThumbPlayback();
    } else if (props.context === 'inline') {
      if (active === true) applyInlineActive();
      else if (active === false || previous === true) el.pause();
      // false -> null: no-op (don't stomp a user-initiated playback).
    }
    // lightbox: `active` is ignored.
  }
);

/* ------------------------------ pdf rendering ----------------------------- */
// The url -> first-page-canvas cache and the pdf.js loading live in the plain
// <script> block above (true module scope, shared across all instances).

async function paintPdf() {
  if (!isPdf.value || props.context === 'lightbox') return;
  if (isLoading.value || isFailed.value) return;
  const target = pdfCanvasEl.value;
  const url = props.url;
  if (!target || !url) return;
  try {
    const source = await renderPdfFirstPage(url);
    // Guard against the element/url changing while we awaited.
    if (pdfCanvasEl.value !== target || props.url !== url) return;
    target.width = source.width;
    target.height = source.height;
    target.getContext('2d').drawImage(source, 0, 0);
  } catch (error) {
    pdfFailed.value = true;
  }
}

// Reset the failure flag and re-attempt only when the source itself changes —
// NOT when `pdfCanvasEl` toggles, otherwise a failed render would unmount the
// canvas, null the ref, reset the flag and remount it in an endless loop.
watch(
  () => [props.url, props.resource && props.resource.name, props.context],
  () => {
    pdfFailed.value = false;
    paintPdf();
  }
);
// Paint once the canvas element actually mounts (ref goes null -> element).
watch(pdfCanvasEl, () => paintPdf());

onBeforeUnmount(() => {
  teardownVideoObserver();
  // Stop any playback right away (spec: unmount behaves like `active: false`).
  if (videoEl.value) videoEl.value.pause();
});
</script>

<style scoped>
.resource-media {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  overflow: hidden;
}

.rm-media {
  width: 100%;
  height: 100%;
  display: block;
}

.fit-cover .rm-media {
  object-fit: cover;
}

.fit-contain .rm-media {
  object-fit: contain;
}

.rm-canvas {
  background: transparent;
}

/* PDF embed fills the frame (used in lightbox). */
.rm-embed {
  width: 100%;
  height: 100%;
  min-height: 70vh;
  border: 0;
  display: block;
}

/* Discreet loading placeholder. */
.rm-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: currentColor;
  opacity: 0.05;
}

.rm-spinner {
  width: 26px;
  height: 26px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  opacity: 0.6;
  animation: rm-spin 0.9s linear infinite;
}

@keyframes rm-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Audio card. */
.rm-audio-card {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  padding: 1em;
  box-sizing: border-box;
  text-align: center;
}

.rm-audio-icon {
  display: inline-flex;
  font-size: 2.4em;
  opacity: 0.85;
}

.rm-audio-title {
  font-size: 0.95em;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.rm-audio-player {
  width: 100%;
  max-width: 320px;
}

.ctx-thumb .rm-audio-icon {
  font-size: 1.8em;
}

.ctx-thumb .rm-audio-title {
  font-size: 0.8em;
  -webkit-line-clamp: 1;
}

.ctx-lightbox .rm-audio-icon {
  font-size: 3.4em;
}

.ctx-lightbox .rm-audio-player {
  max-width: 480px;
}

/* Generic file tile. */
.rm-file-tile {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5em;
  padding: 1em;
  box-sizing: border-box;
  text-align: center;
}

.rm-file-icon {
  display: inline-flex;
  font-size: 2.8em;
  opacity: 0.7;
}

.rm-file-name {
  font-size: 0.85em;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.ctx-thumb .rm-file-icon {
  font-size: 2em;
}
</style>
